import { useEffect, useState } from "react";
import styles from "../../styles/components/searchFigma/CategoryNav.module.css";
import { useRouter } from "next/router";

export default function CategoryNav({
  manufacturerLists,
  distributorLists,
  onFilterApply,
}) {
  const router = useRouter();
  const { source, manufacturer } = router.query;
  // 제조사 / 유통사 화살표 상태
  const [manufacturerArrow, setManufacturerArrow] = useState("none");
  const [distributorArrow, setDistributorArrow] = useState("none");

  // ✅ 제조사 / 유통사 각각의 체크 상태 배열
  const [checkedManufacturer, setCheckedManufacturer] = useState([]);
  const [checkedDistributor, setCheckedDistributor] = useState([]);

  // ✅ 한 줄에 몇 개씩 보여줄지 상태값 추가
  const [itemsPerRow, setItemsPerRow] = useState(5);

  // ✅ 현재 선택된 항목 필터링
  const selectedManufacturers = manufacturerLists.filter(
    (_, i) => checkedManufacturer[i]
  );
  const selectedDistributors = distributorLists.filter(
    (_, i) => checkedDistributor[i]
  );

  // ✅ 브라우저 사이즈 감지해서 반응형 적용
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width >= 601 && width <= 1024) {
        setItemsPerRow(3);
      } else {
        setItemsPerRow(5);
      }
    };

    handleResize(); // 초기값 적용
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // ✅ 리스트 변경 시 각 체크 배열 길이 맞추기
  useEffect(() => {
    const initialStates = Array(distributorLists.length).fill(false);
    if (source) {
      const selectedSources = decodeURIComponent(source).split(",");
      distributorLists.forEach((name, idx) => {
        if (selectedSources.includes(name)) {
          initialStates[idx] = true;
        }
      });
    }
    setCheckedDistributor(initialStates);
  }, [distributorLists, source, itemsPerRow]);

  useEffect(() => {
    const initialStates = Array(manufacturerLists.length).fill(false);
    if (manufacturer) {
      const selectedManufacturers = decodeURIComponent(manufacturer).split(",");

      manufacturerLists.forEach((name, idx) => {
        if (selectedManufacturers.includes(name)) {
          initialStates[idx] = true;
        }
      });
    }
    setCheckedManufacturer(initialStates);
  }, [manufacturerLists, manufacturer, itemsPerRow]);

  // ✅ 각 리스트별 클릭 시 개별 토글 함수
  const toggleManufacturerChecked = (index) => {
    setCheckedManufacturer((prev) => {
      const newState = [...prev];
      newState[index] = !newState[index];
      // ✅ 토글 직후 선택된 리스트 계산
      const newSelectedManufacturers = manufacturerLists.filter(
        (_, i) => newState[i]
      );
      const newSelectedDistributors = distributorLists.filter(
        (_, i) => checkedDistributor[i]
      );
      // ✅ 즉시 필터 적용
      onFilterApply(
        "keyword",
        newSelectedManufacturers,
        newSelectedDistributors
      );
      return newState;
    });
  };

  const toggleDistributorChecked = (index) => {
    setCheckedDistributor((prev) => {
      const newState = [...prev];
      newState[index] = !newState[index];
      const newSelectedManufacturers = manufacturerLists.filter(
        (_, i) => checkedManufacturer[i]
      );
      const newSelectedDistributors = distributorLists.filter(
        (_, i) => newState[i]
      );
      onFilterApply(
        "keyword",
        newSelectedManufacturers,
        newSelectedDistributors
      );
      return newState;
    });
  };

  // ✅ 화살표 토글 함수
  const toggleManufacturerArrow = () => {
    if (manufacturerArrow === "none") setManufacturerArrow("down");
    else if (manufacturerArrow === "down") setManufacturerArrow("up");
    else if (manufacturerArrow === "up") setManufacturerArrow("down");
  };

  const toggleDistributorArrow = () => {
    if (distributorArrow === "none") setDistributorArrow("down");
    else if (distributorArrow === "down") setDistributorArrow("up");
    else if (distributorArrow === "up") setDistributorArrow("down");
  };

  // ✅ 리스트 길이에 따라 화살표 초기 상태 자동 설정
  // ✅ 리스트 길이 및 선택된 항목 위치에 따라 화살표 자동 설정
  useEffect(() => {
    // 기본 화살표 상태
    let manuArrow = manufacturerLists.length > itemsPerRow ? "down" : "none";
    let distArrow = distributorLists.length > itemsPerRow ? "down" : "none";

    // ✅ 제조사: 6번째(즉, index >= itemsPerRow) 이후 체크된 게 있으면 자동 펼침
    const hasCheckedBelow5Manufacturer = checkedManufacturer.some(
      (checked, i) => checked && i >= itemsPerRow
    );
    if (hasCheckedBelow5Manufacturer) manuArrow = "up";

    // ✅ 유통사: 6번째 이후 선택 있으면 자동 펼침
    const hasCheckedBelow5Distributor = checkedDistributor.some(
      (checked, i) => checked && i >= itemsPerRow
    );
    if (hasCheckedBelow5Distributor) distArrow = "up";

    setManufacturerArrow(manuArrow);
    setDistributorArrow(distArrow);
  }, [
    manufacturerLists,
    distributorLists,
    checkedManufacturer,
    checkedDistributor,
  ]);

  return (
    <div className={styles.frame0}>
      <div className={styles.frame001}>
        {/* ✅ 제조사 영역 */}
        <div className={`${styles.frame0011} ${styles.categoryDivider}`}>
          <button
            className={styles.frame00100}
            onClick={toggleManufacturerArrow}
          >
            <div className={`${styles.frame001000} body2`}>제조사</div>
            {manufacturerArrow === "down" && (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                className={styles.frame001001}
              >
                <path
                  d="M19.0607 7.93943C19.6464 8.52522 19.6464 9.47474 19.0607 10.0605L13.0607 16.0605C12.7794 16.3418 12.3979 16.5 12.0001 16.5C11.6023 16.5 11.2209 16.3418 10.9396 16.0605L4.93957 10.0605C4.35378 9.47474 4.35378 8.52522 4.93957 7.93943C5.52535 7.35364 6.47487 7.35364 7.06066 7.93943L12.0001 12.8789L16.9396 7.93943C17.5254 7.35364 18.4749 7.35364 19.0607 7.93943Z"
                  fill="currentColor"
                />
              </svg>
            )}
            {manufacturerArrow === "up" && (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                className={styles.frame001001}
              >
                <path
                  d="M4.93934 16.0605C4.35355 15.4748 4.35355 14.5252 4.93934 13.9394L10.9393 7.93945C11.2206 7.65814 11.6021 7.49999 11.9999 7.49999C12.3977 7.49999 12.7791 7.65814 13.0604 7.93945L19.0604 13.9394C19.6462 14.5252 19.6462 15.4748 19.0604 16.0605C18.4746 16.6463 17.5251 16.6463 16.9393 16.0605L11.9999 11.1211L7.06043 16.0605C6.47465 16.6463 5.52513 16.6463 4.93934 16.0605Z"
                  fill="currentColor"
                />
              </svg>
            )}
          </button>

          <div className={styles.frame00110}>
            {/* 리스트를 5개씩 나눠서 그룹화 */}
            {Array.from(
              {
                length: Math.ceil(manufacturerLists.length / itemsPerRow) || 1,
              },
              (_, rowIndex) => {
                const start = rowIndex * itemsPerRow;
                const end = start + itemsPerRow;
                const items = manufacturerLists.slice(start, end);
                const filled = [...items];
                while (filled.length < itemsPerRow) filled.push("");

                return (
                  <>
                    {(rowIndex === 0 || manufacturerArrow === "up") && (
                      <div key={rowIndex} className={styles.frame00101}>
                        {filled.map((item, i) => {
                          const absoluteIndex = start + i;
                          const isValid = item !== "";
                          const isChecked = checkedManufacturer[absoluteIndex];

                          return item ? (
                            <button
                              key={i}
                              className={`${styles.frame001010} ${
                                isChecked ? styles.categoryListCehcked : ""
                              } label1`}
                              onClick={() =>
                                isValid &&
                                toggleManufacturerChecked(absoluteIndex)
                              }
                              disabled={!isValid}
                            >
                              {isChecked && (
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  viewBox="0 0 16 16"
                                  className={styles.checkedCategory}
                                >
                                  <path
                                    d="M3.33334 8L6.66668 11.3333L13.3333 4.66667"
                                    stroke="currentColor"
                                    strokeWidth="3"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    fill="none"
                                  />
                                </svg>
                              )}
                              {item}
                            </button>
                          ) : (
                            <div
                              key={i}
                              className={`${styles.frame001010}`}
                            ></div>
                          );
                        })}
                      </div>
                    )}
                  </>
                );
              }
            )}
          </div>
        </div>

        {/* ✅ 유통사 영역 */}
        <div className={styles.frame0011}>
          <button
            className={styles.frame00100}
            onClick={toggleDistributorArrow}
          >
            <div className={`${styles.frame001000} body2`}>유통사</div>
            {distributorArrow === "down" && (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                className={styles.frame001001}
              >
                <path
                  d="M19.0607 7.93943C19.6464 8.52522 19.6464 9.47474 19.0607 10.0605L13.0607 16.0605C12.7794 16.3418 12.3979 16.5 12.0001 16.5C11.6023 16.5 11.2209 16.3418 10.9396 16.0605L4.93957 10.0605C4.35378 9.47474 4.35378 8.52522 4.93957 7.93943C5.52535 7.35364 6.47487 7.35364 7.06066 7.93943L12.0001 12.8789L16.9396 7.93943C17.5254 7.35364 18.4749 7.35364 19.0607 7.93943Z"
                  fill="currentColor"
                />
              </svg>
            )}
            {distributorArrow === "up" && (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                className={styles.frame001001}
              >
                <path
                  d="M4.93934 16.0605C4.35355 15.4748 4.35355 14.5252 4.93934 13.9394L10.9393 7.93945C11.2206 7.65814 11.6021 7.49999 11.9999 7.49999C12.3977 7.49999 12.7791 7.65814 13.0604 7.93945L19.0604 13.9394C19.6462 14.5252 19.6462 15.4748 19.0604 16.0605C18.4746 16.6463 17.5251 16.6463 16.9393 16.0605L11.9999 11.1211L7.06043 16.0605C6.47465 16.6463 5.52513 16.6463 4.93934 16.0605Z"
                  fill="currentColor"
                />
              </svg>
            )}
          </button>

          <div className={styles.frame00110}>
            {Array.from(
              { length: Math.ceil(distributorLists.length / itemsPerRow) || 1 },
              (_, rowIndex) => {
                const start = rowIndex * itemsPerRow;
                const end = start + itemsPerRow;
                const items = distributorLists.slice(start, end);
                const filled = [...items];
                while (filled.length < itemsPerRow) filled.push("");

                return (
                  <>
                    {(rowIndex === 0 || distributorArrow === "up") && (
                      <div key={rowIndex} className={styles.frame00101}>
                        {filled.map((item, i) => {
                          const absoluteIndex = start + i;
                          const isValid = item !== "";
                          const isChecked = checkedDistributor[absoluteIndex];

                          return item ? (
                            <button
                              key={i}
                              className={`${styles.frame001010} ${
                                isChecked ? styles.categoryListCehcked : ""
                              } label1`}
                              onClick={() =>
                                toggleDistributorChecked(absoluteIndex)
                              }
                            >
                              {isChecked && (
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  viewBox="0 0 16 16"
                                  className={styles.checkedCategory}
                                >
                                  <path
                                    d="M3.33334 8L6.66668 11.3333L13.3333 4.66667"
                                    stroke="currentColor"
                                    strokeWidth="3"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    fill="none"
                                  />
                                </svg>
                              )}
                              {item}
                            </button>
                          ) : (
                            <div key={i} className={`${styles.frame001010}`} />
                          );
                        })}
                      </div>
                    )}
                  </>
                );
              }
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
